import { Card, Table, TableContainer, TablePagination } from '@mui/material';
import Scrollbar from 'src/components/molecules/scroll-bar';
import ListToolbar from 'src/components/molecules/list-toolbar';
import ListHead from 'src/components/molecules/list-head';
import CustomTableBody from 'src/components/molecules/custom-table-body';
import { count, labelDisplayedRows } from 'src/functions/pagination';
import { createQueryParameter } from 'src/functions/api-parameter';
import { createParams } from 'src/functions/params';
import useStaffInformation from 'src/hooks/use-staff-information';
import { FC } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import useFetch from 'src/hooks/use-fetch';
import { PhotoStudioReservationFrame } from 'codegen/axios/photo/photo_studio';
import { frameListFetchOrganize } from 'src/functions/fetch-value-organize';
import NotFoundTableBody from 'src/components/molecules/not-found-table-body';
import FilterNoneIcon from '@mui/icons-material/FilterNone';
import { useFrameCopy } from './hooks';

export const FrameList: FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  /* 定数宣言 */
  const rowsPerPage = 30;
  const periodStart = searchParams.get('periodStart') ?? null;
  const periodEnd = searchParams.get('periodEnd') ?? null;

  /* hooks */
  const { doCopy, loading: copyLoading } = useFrameCopy();
  const { pathname, search } = useLocation();
  const staff = useStaffInformation();
  const navigate = useNavigate();
  const page = Number(searchParams.get('page')) ?? 0;
  const searchName = searchParams.get('searchName') ?? '';
  const query = createQueryParameter({
    searchName: { key: 'searchName', value: searchName },
    periodStart: { key: 'startDatetime', value: periodStart },
    periodEnd: { key: 'endDatetime', value: periodEnd },
  });

  const TABLE_HEAD = [
    {
      id: 'copy',
      label: '複製',
      align: 'center',
      buttonProperty: {
        variant: 'subtitle2',
        noWrap: true,
        handleClick: (_: any, id: any) => doCopy(id),
        disabled: copyLoading,
      },
      icon: <FilterNoneIcon />,
    },
    { id: 'name', label: '予約枠名', align: 'left', typographyProperty: { variant: 'subtitle2', noWrap: true } },
    { id: 'photoStudioName', label: '店舗名', align: 'left' },
    { id: 'validPeriod', label: '有効期間', align: 'left' },
    { id: 'validDay', label: '無効曜日', align: 'center' },
    { id: 'validTime', label: '有効時間', align: 'center' },
    {
      id: 'pageViewFlg',
      label: '表示',
      align: 'left',
      labelProperty: {
        表示: 'success',
        非表示: 'warning',
      },
    },
    { id: 'unitTimeMin', label: '最小単位時間', align: 'center' },
    {
      id: 'setStatus',
      label: '設定状態',
      align: 'left',
      labelProperty: {
        正常: 'success',
        未設定: 'warning',
      },
    },
  ];
  /* データ取得 */
  const { fetchedData: responseData, loading } = useFetch<Array<PhotoStudioReservationFrame>>({
    iniState: null,
    reqProp: {
      pathKey: 'frame',
      queryParams: {
        offset: page * rowsPerPage,
        limit: rowsPerPage,
        ...query,
      },
    },
    observable: [searchParams, copyLoading],
    organize: frameListFetchOrganize,
  });

  // 次のページがあるかどうか
  const { fetchedData: nextPageCheck, loading: loadingNextPageCheck } = useFetch<boolean>({
    reqProp: {
      pathKey: 'frame',
      queryParams: {
        offset: (page + 1) * rowsPerPage,
        limit: 2,
        ...query,
      },
    },
    observable: [searchParams, copyLoading],
  });

  /* handle */
  const handleChangePage = (_: any, newPage: number) => {
    setSearchParams(createParams({ keyValues: { page: newPage }, searchParams }));
  };
  const handleSearchByName = (value: string) => {
    setSearchParams(createParams({ keyValues: { searchName: value }, searchParams, page: 0 }));
  };
  const handleRowClick = (id: string) => {
    // deleteとかで使うために検索条件を保存
    localStorage.setItem(`photoStudioStaff/setting/frame-search`, search);
    navigate(`/${staff.userType}/setting/frame/${id}`);
  };

  const handlePeriodStart = (value: any) => {
    setSearchParams(createParams({ keyValues: { periodStart: value }, searchParams, page: 0 }));
  };
  const handlePeriodEnd = (value: any) => {
    setSearchParams(createParams({ keyValues: { periodEnd: value }, searchParams, page: 0 }));
  };

  // eslint-disable-next-line no-nested-ternary
  const emptyRows = responseData ? (page > 0 ? Math.max(0, rowsPerPage - responseData.length) : 0) : 0;
  const isUserNotFound = !responseData || responseData.length === 0;

  return (
    <Card sx={{ mt: 5 }}>
      <ListToolbar
        periodStart={periodStart}
        periodEnd={periodEnd}
        handlePeriodStart={handlePeriodStart}
        handlePeriodEnd={handlePeriodEnd}
        searchName={searchName}
        handleSearchByName={handleSearchByName}
        placeholder={'予約枠名、店舗名'}
        width={350}
      />

      <Scrollbar>
        <TableContainer sx={{ minWidth: 800 }}>
          <Table>
            <ListHead headLabel={TABLE_HEAD} />

            <CustomTableBody
              {...{
                TABLE_HEAD,
                responseData,
                handleRowClick,
                emptyRows,
                rowsPerPage,
                loading: loading || loadingNextPageCheck,
              }}
            />

            {isUserNotFound && <NotFoundTableBody searchName={searchName} />}
          </Table>
        </TableContainer>
      </Scrollbar>

      {!isUserNotFound && (
        <TablePagination
          rowsPerPageOptions={[30]}
          component="div"
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          count={count(nextPageCheck, rowsPerPage, page)}
          labelDisplayedRows={({ from, to }) => labelDisplayedRows(responseData, nextPageCheck, from, to)}
        />
      )}
    </Card>
  );
};
